import { stationClientSchema } from './paired.schema'
import { z } from 'zod'
import { fromZodError } from 'zod-validation-error'

import {
  makeServerResponseSchema,
  makePaginatedServerResponseSchema,
} from '../../utils/schema'
import { client } from '../../services/client'
import {
  activeStationsResponseSchema,
  clientStationSchema,
  stationResponseSchema,
  stationsResponseSchema,
} from './paired.schema'

import type { TerminalType } from './paired.schema'
import type { CheckInQueryKeys } from './queries'

async function fetchStationClient(objectId: string | undefined) {
  if (!objectId) return Promise.reject(new Error('No station client found.'))

  const response = await client
    .post('functions/getStationClient', { json: { objectId } })
    .json()

  const parsedResponse = makeServerResponseSchema(
    z.object({ stationClient: stationClientSchema })
  ).safeParse(response)

  if (!parsedResponse.success)
    throw new Error(fromZodError(parsedResponse.error).message)
  return parsedResponse.data.result
}

async function fetchActiveStation({
  deviceToken,
  type,
}: {
  deviceToken: string | null
  type: TerminalType
}) {
  if (!deviceToken)
    throw new Error(
      'Push notification permissions were not granted. Please reset permissions and allow.'
    )

  const response = await client
    .post('functions/getStationActive', {
      json: { deviceToken, type },
    })
    .json()

  const parsedResponse = makeServerResponseSchema(
    stationResponseSchema
  ).safeParse(response)

  if (!parsedResponse.success)
    throw new Error(fromZodError(parsedResponse.error).message)

  return parsedResponse.data.result.station
}

async function fetchActiveStations({
  deviceToken,
}: {
  deviceToken: string | null
}) {
  const response = await client
    .post('functions/getStationsActive', {
      json: { deviceToken },
    })
    .json()

  const parsedResponse = makePaginatedServerResponseSchema(
    activeStationsResponseSchema
  ).safeParse(response)

  if (!parsedResponse.success)
    throw new Error(fromZodError(parsedResponse.error).message)

  return parsedResponse.data.result
}

async function fetchClientStations({
  queryKey: [{ stationId }],
  pageParam = 0,
}: CheckInQueryKeys['clientStationsById']) {
  const response = await client
    .post('functions/getStationClients', {
      json: {
        page: pageParam,
        pageSize: 10,
        stationId,
      },
    })
    .json()

  const parsedResponse = makePaginatedServerResponseSchema(
    z.array(clientStationSchema)
  ).safeParse(response)

  if (!parsedResponse.success)
    throw new Error(fromZodError(parsedResponse.error).message)

  return parsedResponse.data.result
}

async function fetchStations() {
  const response = await client.post('functions/getStations').json()

  const parsedResponse = makePaginatedServerResponseSchema(
    stationsResponseSchema
  ).safeParse(response)
  if (!parsedResponse.success)
    throw new Error(fromZodError(parsedResponse.error).message)

  return parsedResponse.data.result
}

export {
  fetchActiveStation,
  fetchActiveStations,
  fetchClientStations,
  fetchStationClient,
  fetchStations,
}
